import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';

// our component
import { Query } from 'components/common/Query';
import ContentLoader from 'react-content-loader';
import CardBody from 'components/UI/Card/CardBody';
import Card from 'components/UI/Card/Card';
import { PROCESSOR_LIST_NAMES } from 'components/Radar/List';
import ErrorView from 'helpers/errorViewHelper';
import getProcessorTargetVariables from 'helpers/processorTargetVariables';
import { isProcessorListItemAllowedByNames } from 'helpers/processorTargetGuards';
import ItemDetail from './Details';

export const ITEM_PROCESSOR_DETAIL_QUERY = gql`
  query Processor($id: Int!) {
    processorListItem(id: $id) {
      id
      listName
      value
      group
      description
      createdBy {
        email
        fullname
      }
      updatedBy {
        email
        fullname
      }
    }
  }
`;

const LoadingDetail = () => (
  <Card>
    <CardBody>
      <ContentLoader height={554} width={489} speed={2}>
        <rect x="24" y="25" rx="8" ry="8" width="192" height="32" />
        <rect x="24" y="118" rx="4" ry="4" width="145" height="18" />
        <rect x="24" y="168" rx="4" ry="4" width="313" height="18" />
        <rect x="24" y="219" rx="4" ry="4" width="224" height="18" />
        <rect x="24" y="270" rx="4" ry="4" width="163" height="18" />
        <rect x="24" y="322" rx="4" ry="4" width="88" height="18" />
        <rect x="24" y="372" rx="4" ry="4" width="196" height="18" />
        <rect x="24" y="423" rx="4" ry="4" width="140" height="18" />
        <rect x="24" y="474" rx="4" ry="4" width="258" height="18" />
      </ContentLoader>
    </CardBody>
  </Card>
);

const TargetGuardedDetail = ({
  data,
  initialFilters,
  listNames,
  loading,
  loadingListNames,
  routePaths,
  target,
}) => {
  if (loading || loadingListNames) {
    return <LoadingDetail />;
  }

  const { processorListItem } = data;
  if (
    !isProcessorListItemAllowedByNames(processorListItem, listNames, target)
  ) {
    return <ErrorView message="Permiso denegado" />;
  }

  return (
    <ItemDetail
      data={processorListItem}
      initialFilters={initialFilters}
      routePaths={routePaths}
    />
  );
};

TargetGuardedDetail.propTypes = {
  data: PropTypes.shape({
    processorListItem: PropTypes.instanceOf(Object),
  }).isRequired,
  initialFilters: PropTypes.instanceOf(Object).isRequired,
  listNames: PropTypes.instanceOf(Array),
  loading: PropTypes.bool.isRequired,
  loadingListNames: PropTypes.bool.isRequired,
  routePaths: PropTypes.shape({}),
  target: PropTypes.string,
};

TargetGuardedDetail.defaultProps = {
  listNames: undefined,
  routePaths: undefined,
  target: undefined,
};

const RadarItemDetailContainer = ({
  id,
  initialFilters,
  routePaths,
  target,
}) => (
  <Query
    query={ITEM_PROCESSOR_DETAIL_QUERY}
    variables={{ id }}
    notifyOnNetworkStatusChange
    fetchPolicy="cache-and-network"
  >
    {({ data, loading }) =>
      target ? (
        <Query
          query={PROCESSOR_LIST_NAMES}
          variables={getProcessorTargetVariables(target)}
          fetchPolicy="cache-and-network"
        >
          {({ data: listNamesData, loading: loadingListNames }) => (
            <TargetGuardedDetail
              data={data}
              initialFilters={initialFilters}
              listNames={listNamesData.processorListNames}
              loading={loading}
              loadingListNames={loadingListNames}
              routePaths={routePaths}
              target={target}
            />
          )}
        </Query>
      ) : (
        <TargetGuardedDetail
          data={data}
          initialFilters={initialFilters}
          loading={loading}
          loadingListNames={false}
          routePaths={routePaths}
          target={target}
        />
      )
    }
  </Query>
);

RadarItemDetailContainer.propTypes = {
  id: PropTypes.string.isRequired,
  initialFilters: PropTypes.instanceOf(Object).isRequired,
  routePaths: PropTypes.shape({}),
  target: PropTypes.string,
};

RadarItemDetailContainer.defaultProps = {
  routePaths: undefined,
  target: undefined,
};

export default RadarItemDetailContainer;
